/**
 * ClearButton React components serves as a fallback for input[type="search"] on Firefox.
 * This browser don't implement native clear button.
 *
 * It is tightly coupled with Input component impl and work basically for SearchInput component.
 */
import React from "react";
import { styled } from "@linaria/react";
import { colors } from "../Theme";
import CloseCircleFill from "../IconV2/react/CloseCircleFill";

const Button = styled.button`
  all: unset;
  align-items: center;
  background-color: ${colors.hydrogen};
  color: ${colors.titanium500};
  display: flex;
  height: 100%;
  padding-right: 16px;
  transition: color 144ms linear;

  &:hover {
    cursor: pointer;
    color: ${colors.titanium700};
  }

  input:placeholder-shown + & {
    display: none;
  }

  &:not(:active):not(:focus) {
    input:not(:focus-within) + & {
      display: none;
    }
  }
`;

const HTMLInputSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
const HTMLSelectSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")?.set;

function ClearButton({
  target,
  ...props
}: {
  target?: React.RefObject<HTMLInputElement | HTMLSelectElement>;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const handleClear = React.useCallback(() => {
    if (!target?.current) return;
    const isInput = target.current instanceof HTMLInputElement;
    const isSelect = target.current instanceof HTMLSelectElement;
    if (isInput) HTMLInputSetter?.call(target.current, "");
    if (isSelect) HTMLSelectSetter?.call(target.current, "");
    target.current.dispatchEvent(new Event(isSelect ? "change" : "input", { bubbles: true }));
    target.current.focus();
  }, [target]);

  return (
    <Button tabIndex={-1} type="button" onClick={handleClear} {...props}>
      <CloseCircleFill size="m" />
    </Button>
  );
}

export default React.memo(ClearButton);
